import React, {useState} from 'react';
import {BookOpen, Loader2, AlertTriangle, CheckCircle} from 'lucide-react';

const AddBook = () => {
    const [formData, setFormData] = useState({
        name: '',
        author: '',
        volume: '',
        sect: '',
        type: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setSuccess(false);

        try {
            const accessToken = localStorage.getItem('accessToken');
            const apiUrl = 'https://api.digitalaalim.com/admin/add-book/';

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                throw new Error(`Failed to add book: ${response.statusText}`);
            }

            // Clear form after successful addition
            setFormData({
                name: '',
                author: '',
                volume: '',
                sect: '',
                type: ''
            });

            setSuccess(true);

            // Hide success message after 3 seconds
            setTimeout(() => {
                setSuccess(false);
            }, 3000);
        } catch (error) {
            console.error('Error adding book:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            {/* Success message */}
            {success && (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                    <div className="flex items-center">
                        <CheckCircle size={20} className="text-green-500 mr-2 flex-shrink-0"/>
                        <p className="text-green-700">Book added successfully!</p>
                    </div>
                </div>
            )}

            {/* Error message */}
            {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                    <div className="flex items-center">
                        <AlertTriangle size={20} className="text-red-500 mr-2 flex-shrink-0"/>
                        <p className="text-red-700">{error}</p>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Book Name */}
                <div>
                    <label htmlFor="book-name" className="block text-sm font-medium text-gray-700 mb-1">
                        Book Name *
                    </label>
                    <input
                        type="text"
                        id="book-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Enter book name"
                    />
                </div>

                {/* Author */}
                <div>
                    <label htmlFor="book-author" className="block text-sm font-medium text-gray-700 mb-1">
                        Author *
                    </label>
                    <input
                        type="text"
                        id="book-author"
                        name="author"
                        value={formData.author}
                        onChange={handleInputChange}
                        required
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Enter author name"
                    />
                </div>

                {/* Volume */}
                <div>
                    <label htmlFor="book-volume" className="block text-sm font-medium text-gray-700 mb-1">
                        Volume
                    </label>
                    <input
                        type="text"
                        id="book-volume"
                        name="volume"
                        value={formData.volume}
                        onChange={handleInputChange}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Volume number or identifier"
                    />
                </div>

                {/* Sect */}
                <div>
                    <label htmlFor="book-sect" className="block text-sm font-medium text-gray-700 mb-1">
                        Sect
                    </label>
                    <select
                        id="book-sect"
                        name="sect"
                        value={formData.sect}
                        onChange={handleInputChange}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                        <option value="">Select Sect</option>
                        <option value="Hanafi">Hanafi</option>
                        <option value="Jafari">Jafari</option>
                        <option value="Hanbali">Hanbali</option>
                        <option value="Shafi">Shafi</option>
                        <option value="Malaki">Malaki</option>
                    </select>
                </div>

                {/* Type */}
                <div>
                    <label htmlFor="book-type" className="block text-sm font-medium text-gray-700 mb-1">
                        Type *
                    </label>
                    <input
                        type="text"
                        id="book-type"
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Book type (e.g., Hadith, Fiqh, Tafsir)"
                    />
                </div>

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {isLoading ? (
                        <>
                            <Loader2 size={16} className="animate-spin mr-2"/>
                            Adding...
                        </>
                    ) : (
                        <>
                            <BookOpen size={16} className="mr-2"/>
                            Add Book
                        </>
                    )}
                </button>
            </form>
        </div>
    );
};

export default AddBook;